import { AfterViewInit, Component, ElementRef, HostBinding, inject, OnDestroy, viewChild } from "@angular/core";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import { TranslateModule, TranslateParser } from "@ngx-translate/core";
import { MainService } from "core/main.service";
import { NotificationService } from "core/notification.service";
import { LanguageService } from "core/translate/language.service";
import { StagesTranslateParser } from "core/translate/stages-translate.parser";
import { ApplicationState, State } from "core/volatile-application-state.model";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppLoadService } from "app/app-load.service";
import { MaintenanceService } from "common/maintenance/maintenance.service";
import { ApplicationContextRepository } from "core/application-context.repository";
import { ConfirmMaintenanceComponent } from "common/maintenance/confirm-maintenance.component";
import { AsyncPipe, NgClass } from "@angular/common";
import { DisclaimerLoginComponent } from "common/disclaimer/login/disclaimer-login.component";
import { MenuComponent } from "common/menu/menu.component";
import { NavigationDrawerComponent } from "navigation/drawer/navigation-drawer.component";
import { GlobalSearchBoxComponent } from "search/search.component";
import { SnackbarComponent } from "common/snackbar/snackbar.component";
import { SafePipe } from "common/download/safe.pipe";
import { LayoutService } from "core/layout.service";
import { RightNavigationRailComponent } from "common/rail/right-navigation-rail.component";
import { SidesheetComponent } from "common/sidesheet/sidesheet.component";
import { ChatbotComponent } from "chatbot/chatbot.component";
import MaintenancePushNotification = stages.core.maintenance.MaintenancePushNotification;

@Component({
	selector: "stages-main",
	templateUrl: "./main.component.html",
	styleUrls: ["./main.component.scss"],
	imports: [
		DisclaimerLoginComponent,
		NgClass,
		MenuComponent,
		NavigationDrawerComponent,
		RouterOutlet,
		GlobalSearchBoxComponent,
		SnackbarComponent,
		AsyncPipe,
		TranslateModule,
		SafePipe,
		RightNavigationRailComponent,
		SidesheetComponent,
		ChatbotComponent,
	],
})
export class MainComponent implements OnDestroy, AfterViewInit {
	protected mainService = inject(MainService);
	private route = inject(ActivatedRoute);
	private languageService = inject(LanguageService);
	private translateParser = inject(TranslateParser);
	private notificationService = inject(NotificationService);
	private appLoadService = inject(AppLoadService);
	private maintenanceService = inject(MaintenanceService);
	protected layoutService = inject(LayoutService);

	readonly drawerRef = viewChild.required("navigationDrawer", { read: ElementRef });

	@HostBinding("class.sidesheet-open")
	sidesheetOpen = false;

	private destroy$ = new Subject<boolean>();
	private chatbotMenuItem = {
		iconClass: "ico ico-chatbot-side",
		name: "chatbot.name",
		isSwitch: false,
		on: () => {
			this.mainService.applicationState.toggleChatbot();
		},
	};

	applicationState: ApplicationState;
	menuItems: MenuItem[] = [];
	extraMenuItems: MenuItem[] = [];
	menuButtons: Button[] = [];
	dialogActive: boolean = false;
	searchOpen: boolean = false;
	bannerEnabled: boolean = false;
	sudoEnabled: boolean = false;
	private oldToolbarVisibleState: boolean = false;
	isDisclaimerEnabled = false;
	disclaimerVersion: string | null = null;
	chatbotEnabled: boolean = false;
	showSideSheet: boolean = false;

	constructor() {
		const mainService = this.mainService;
		const applicationContextRepository = inject(ApplicationContextRepository);

		this.applicationState = mainService.applicationState;

		// update language setting on workspace change
		this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
			(this.translateParser as StagesTranslateParser).setCurrentMessageScope(
				data.workspaceView.currentWorkspace.messageScope,
			);
			this.languageService.determineAndApplyLanguage(
				this.route.snapshot.paramMap.get("processVersion")!,
				data.applicationContext.preferredLanguage,
			);

			this.bannerEnabled = data.applicationContext.bannerEnabled;
			this.disclaimerVersion = data.applicationContext.disclaimerVersion;
			this.chatbotEnabled =
				data.applicationContext.views.length > 0 && data.applicationContext.aiAcknowledgementsState !== "DECLINED";
			this.showSideSheet = this.chatbotEnabled;
		});
		applicationContextRepository.applicationContext$.pipe(takeUntil(this.destroy$)).subscribe((applicationContext) => {
			this.chatbotEnabled =
				applicationContext.views.length > 0 && applicationContext.aiAcknowledgementsState !== "DECLINED";
			if (this.chatbotEnabled) {
				this.menuItems = [this.chatbotMenuItem, ...this.extraMenuItems];
			} else {
				this.menuItems = [...this.extraMenuItems];
				this.closeSidesheet();
			}
			this.sudoEnabled = applicationContext.superUser && applicationContext.username !== "root";
			this.isDisclaimerEnabled = applicationContext.disclaimerEnabled;
			this.showSideSheet = this.chatbotEnabled;
		});

		this.mainService.menuItems$.pipe(takeUntil(this.destroy$)).subscribe((menuItems) => {
			this.extraMenuItems = menuItems;
			this.menuItems = this.chatbotEnabled ? [this.chatbotMenuItem, ...this.extraMenuItems] : [...menuItems];
		});

		this.mainService.menuButtons$.pipe(takeUntil(this.destroy$)).subscribe((menuButtons) => {
			this.menuButtons = menuButtons;
		});

		this.notificationService.subscribe("system", {});
		this.notificationService.setHandlers("system", {
			MAINTENANCE_STARTED: (maintenanceInfo: MaintenancePushNotification) => {
				this.maintenanceService.notify(
					"administration.maintenance.pushNotification.start.title",
					"administration.maintenance.pushNotification.start.message",
					maintenanceInfo,
					"STARTED",
					ConfirmMaintenanceComponent,
					true,
				);
			},
			MAINTENANCE_ANNOUNCED: (maintenanceInfo: MaintenancePushNotification) => {
				this.maintenanceService.notify(
					"administration.maintenance.pushNotification.announce.title",
					"administration.maintenance.pushNotification.announce.message",
					maintenanceInfo,
					"ANNOUNCED",
					ConfirmMaintenanceComponent,
					true,
				);
			},
		});

		this.applicationState.isSearchOpen.pipe(takeUntil(this.destroy$)).subscribe((isSearchOpen: boolean) => {
			this.searchOpen = isSearchOpen;
		});

		this.applicationState.isChatbotOpen.pipe(takeUntil(this.destroy$)).subscribe((sidesheetOpen: boolean) => {
			this.sidesheetOpen = sidesheetOpen;
		});
	}

	onChangeSudoMode(): void {
		this.mainService.openPopup(["user", "sudo"], this.route);
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();

		this.notificationService.unsubscribe("system");
	}

	onDialogActivate(): void {
		this.dialogActive = true;
		this.oldToolbarVisibleState = this.applicationState.toolbarVisible;
		// timeout needed in order to come after ProcessViewComponent that also uses timeout
		setTimeout(() => {
			this.applicationState.toolbarVisible = false;
		});
	}

	onDialogDeactivate(): void {
		this.dialogActive = false;
		this.applicationState.toolbarVisible = this.oldToolbarVisibleState;
	}

	onPopupOpened($event: Event): void {
		this.mainService.popupOpenState$.next(true);
	}

	onPopupClosed($event: Event): void {
		this.mainService.popupOpenState$.next(false);
	}

	ngAfterViewInit(): void {
		this.appLoadService.resolveBootstrapProperties().then((properties) => {
			if (properties.maintenance.maintenanceMode === "ANNOUNCED") {
				this.maintenanceService.notify(
					"administration.maintenance.pushNotification.announce.title",
					"administration.maintenance.pushNotification.announce.message",
					properties.maintenance,
					"ANNOUNCED",
					ConfirmMaintenanceComponent,
				);
			} else if (properties.maintenance.maintenanceMode === "STARTED") {
				this.maintenanceService.notify(
					"administration.maintenance.pushNotification.start.title",
					"administration.maintenance.pushNotification.start.message",
					properties.maintenance,
					"STARTED",
					ConfirmMaintenanceComponent,
				);
			}
		});
	}

	protected toggleSearch(): void {
		this.applicationState.toggleSearch();
	}

	protected closeSidesheet(): void {
		if (this.sidesheetOpen) {
			this.applicationState.toggleChatbot();
		}
	}

	protected readonly State = State;
}
